import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Upload, ArrowLeft, CheckCircle, Copy, Mail, Image, X, FileText, Edit2, Save, AlertCircle, Link2 } from 'lucide-react';
import API_URL from '../config/api';
import { useClinic } from '../contexts/ClinicContext';
import { getAvailableTemplates } from '../config';

// Gensolve-style colors
const colors = {
  sidebarBg: '#e8941c',
  headerBg: '#f5f5f5',
  dialogBg: '#6b9bd1',
  dialogBody: '#b8d4f0',
  tableHeader: '#e8e8e8',
  success: '#5cb85c',
  warning: '#f0ad4e',
  danger: '#d9534f',
};

/**
 * Normalise a date string to dd/mm/yyyy (NZ format) for display.
 * Handles yyyy-MM-dd, dd-mm-yyyy, "5 February 2018", and already-correct dd/mm/yyyy.
 */
function normaliseDate(dateStr) {
  if (!dateStr) return '';
  // Already dd/mm/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  // yyyy-MM-dd (ISO)
  const isoMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
  }
  // dd-mm-yyyy
  const dashMatch = dateStr.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dashMatch) {
    const [, day, month, year] = dashMatch;
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
  }
  // dd/mm/yyyy with single digits
  const slashMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
  }
  // "5 February 2018" or "05 Feb 2018"
  const MONTHS = { jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
    jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12' };
  const namedMatch = dateStr.match(/^(\d{1,2})\s+([a-zA-Z]+)\s+(\d{4})$/);
  if (namedMatch) {
    const [, day, monthStr, year] = namedMatch;
    const monthKey = monthStr.toLowerCase().substring(0, 3);
    if (MONTHS[monthKey]) return `${day.padStart(2, '0')}/${MONTHS[monthKey]}/${year}`;
  }
  return dateStr;
}

function UploadReferralPage() {
  const { activeClinicId } = useClinic();
  const [file, setFile] = useState(null);
  const [imagingFiles, setImagingFiles] = useState([]);
  const [injuryType, setInjuryType] = useState('KNEE');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedPatient, setEditedPatient] = useState(null);
  const navigate = useNavigate();

  const injuryTypes = getAvailableTemplates()
    .map(t => ({ value: t.id, label: t.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    const allowedExtensions = ['.pdf', '.docx', '.doc'];
    const ext = selectedFile?.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
    
    if (selectedFile && allowedExtensions.includes(ext)) {
      setFile(selectedFile);
      setError('');
    } else {
      setError('Please select a PDF or Word document');
      setFile(null);
    }
  };

  const handleImagingChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => {
      const ext = f.name.toLowerCase().substring(f.name.lastIndexOf('.'));
      return ['.jpg', '.jpeg', '.png', '.pdf', '.dcm'].includes(ext);
    });
    setImagingFiles(prev => [...prev, ...validFiles]);
  };

  const removeImagingFile = (index) => {
    setImagingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a referral file');
      return;
    }

    setUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('referral', file);
    formData.append('injuryType', injuryType);
    imagingFiles.forEach(imgFile => {
      formData.append('imaging', imgFile);
    });

    try {
      const clinicQuery = activeClinicId ? `?clinicId=${activeClinicId}` : '';
      const response = await axios.post(`${API_URL}/referrals/upload${clinicQuery}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(response.data);
      const patient = response.data.patient;
      setEditedPatient({
        name: patient?.name || '',
        firstName: patient?.firstName || '',
        lastName: patient?.lastName || '',
        dateOfBirth: normaliseDate(patient?.dateOfBirth) || '',
        nhi: patient?.nhi || '',
        phone: patient?.phone || '',
        email: patient?.email || '',
        address: patient?.address || ''
      });
      
      // Auto-enable editing if patient details are missing
      const needsEditing = !patient?.name || patient.name === 'Unknown Patient' || !patient?.email || !patient?.phone;
      if (needsEditing) {
        setIsEditing(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSavePatientDetails = async () => {
    if (!result?.patient?.id) return;
    
    setSaving(true);
    try {
      let fullName = editedPatient.name;
      if (!fullName || fullName === 'Unknown Patient') {
        if (editedPatient.firstName || editedPatient.lastName) {
          fullName = `${editedPatient.firstName} ${editedPatient.lastName}`.trim();
        }
      }

      const response = await axios.put(`${API_URL}/patients/${result.patient.id}`, {
        name: fullName,
        firstName: editedPatient.firstName,
        lastName: editedPatient.lastName,
        dateOfBirth: editedPatient.dateOfBirth,
        nhi: editedPatient.nhi,
        phone: editedPatient.phone,
        email: editedPatient.email,
        address: editedPatient.address
      });

      setResult(prev => ({
        ...prev,
        patient: { ...prev.patient, ...response.data, name: fullName || response.data.name }
      }));
      setEditedPatient(prev => ({ ...prev, name: fullName || response.data.name }));
      setIsEditing(false);
      alert('Patient details saved!');
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = async () => {
    if (!result?.patient?.id || !editedPatient?.email) {
      alert('Please add an email address first');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/patients/${result.patient.id}/send-form-email`);
      if (response.data.success) {
        alert('Email sent successfully to ' + editedPatient.email);
        setResult(prev => ({ ...prev, emailSent: true }));
      } else {
        alert('Failed to send email: ' + (response.data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Failed to send email: ' + (err.response?.data?.error || err.message));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Link copied to clipboard!');
  };

  const getFormLink = () => {
    return result?.patient?.formUrl || result?.patient?.patientLink || '';
  };

  // Show result after successful upload
  if (result) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f0f0f0', fontFamily: 'Segoe UI, Tahoma, sans-serif' }}>
        {/* Header */}
        <div style={{ backgroundColor: colors.headerBg, borderBottom: '1px solid #ccc', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', backgroundColor: '#e0e0e0', border: '1px solid #999', cursor: 'pointer', fontSize: '12px' }}
          >
            <ArrowLeft style={{ width: '14px', height: '14px' }} />
            Back to Dashboard
          </button>
          <span style={{ fontWeight: '600' }}>Upload Referral - Complete</span>
        </div>

        <div style={{ maxWidth: '900px', margin: '24px auto', padding: '0 16px' }}>
          {/* Success Message */}
          <div style={{ backgroundColor: '#d4edda', border: '1px solid #c3e6cb', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CheckCircle style={{ width: '20px', height: '20px', color: '#28a745' }} />
            <span style={{ color: '#155724', fontWeight: '500' }}>Referral uploaded successfully!</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Patient Details Panel */}
            <div style={{ backgroundColor: 'white', border: '1px solid #ccc' }}>
              <div style={{ 
                backgroundColor: colors.dialogBg, padding: '8px 16px', color: 'white', fontWeight: '600',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <span>Patient Details</span>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    style={{ padding: '2px 8px', backgroundColor: 'white', color: colors.dialogBg, border: 'none', cursor: 'pointer', fontSize: '11px' }}
                  >
                    <Edit2 style={{ width: '10px', height: '10px', marginRight: '4px', display: 'inline' }} />
                    Edit
                  </button>
                )}
              </div>
              <div style={{ padding: '16px' }}>
                {isEditing ? (
                  <div style={{ fontSize: '12px' }}>
                    {[
                      { label: 'First Name', key: 'firstName' },
                      { label: 'Last Name', key: 'lastName' },
                      { label: 'Date of Birth', key: 'dateOfBirth', placeholder: 'dd/mm/yyyy' },
                      { label: 'NHI', key: 'nhi' },
                      { label: 'Phone', key: 'phone' },
                      { label: 'Email', key: 'email', type: 'email' },
                      { label: 'Address', key: 'address' },
                    ].map(field => (
                      <div key={field.key} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <label style={{ width: '100px', fontWeight: '500' }}>{field.label}</label>
                        <input
                          type={field.type || 'text'}
                          value={editedPatient[field.key] || ''}
                          placeholder={field.placeholder || ''}
                          onChange={(e) => setEditedPatient({ ...editedPatient, [field.key]: e.target.value })}
                          style={{ flex: 1, padding: '6px', border: '1px solid #ccc', fontSize: '12px' }}
                        />
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                      <button
                        onClick={handleSavePatientDetails}
                        disabled={saving}
                        style={{ 
                          padding: '6px 16px', backgroundColor: colors.success, color: 'white',
                          border: 'none', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '12px'
                        }}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        style={{ padding: '6px 16px', backgroundColor: '#e0e0e0', border: '1px solid #999', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                    <tbody>
                      {[
                        { label: 'Name', value: editedPatient?.name || result.patient?.name },
                        { label: 'DOB', value: normaliseDate(editedPatient?.dateOfBirth || result.patient?.dateOfBirth) },
                        { label: 'NHI', value: editedPatient?.nhi || result.patient?.nhi },
                        { label: 'Phone', value: editedPatient?.phone || result.patient?.phone },
                        { label: 'Email', value: editedPatient?.email || result.patient?.email },
                        { label: 'Address', value: editedPatient?.address || result.patient?.address },
                      ].map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '6px', fontWeight: '500', backgroundColor: '#f9f9f9', width: '100px' }}>{row.label}</td>
                          <td style={{ padding: '6px' }}>{row.value || <span style={{ color: '#999' }}>Not provided</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Form Link Panel */}
            <div style={{ backgroundColor: 'white', border: '1px solid #ccc' }}>
              <div style={{ backgroundColor: colors.dialogBg, padding: '8px 16px', color: 'white', fontWeight: '600' }}>
                Patient Form Link
              </div>
              <div style={{ padding: '16px' }}>
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 12px' }}>
                  Share this link with the patient to complete their questionnaire:
                </p>
                
                <div style={{ backgroundColor: '#f5f5f5', padding: '8px', border: '1px solid #ccc', marginBottom: '12px', wordBreak: 'break-all', fontSize: '11px' }}>
                  {getFormLink() || 'No link generated'}
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => copyToClipboard(getFormLink())}
                    style={{ 
                      padding: '8px 16px', backgroundColor: colors.dialogBg, color: 'white',
                      border: 'none', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px'
                    }}
                  >
                    <Copy style={{ width: '12px', height: '12px' }} />
                    Copy Link
                  </button>
                  
                  <button
                    onClick={handleSendEmail}
                    disabled={!editedPatient?.email}
                    style={{ 
                      padding: '8px 16px', 
                      backgroundColor: editedPatient?.email ? colors.success : '#ccc', 
                      color: 'white',
                      border: 'none', 
                      cursor: editedPatient?.email ? 'pointer' : 'not-allowed', 
                      fontSize: '12px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px'
                    }}
                  >
                    <Mail style={{ width: '12px', height: '12px' }} />
                    Email to Patient
                  </button>
                </div>

                {result.emailSent && (
                  <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#d4edda', fontSize: '12px', color: '#155724' }}>
                    âœ“ Email sent successfully
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Referral Info */}
          <div style={{ backgroundColor: 'white', border: '1px solid #ccc', marginTop: '16px' }}>
            <div style={{ backgroundColor: colors.dialogBg, padding: '8px 16px', color: 'white', fontWeight: '600' }}>
              Referral Information
            </div>
            <div style={{ padding: '16px' }}>
              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '6px', fontWeight: '500', backgroundColor: '#f9f9f9', width: '150px' }}>File Name</td>
                    <td style={{ padding: '6px' }}>{result.referral?.fileName || file?.name}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '6px', fontWeight: '500', backgroundColor: '#f9f9f9' }}>Injury Type</td>
                    <td style={{ padding: '6px' }}>{injuryType.replace('_', ' ')}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '6px', fontWeight: '500', backgroundColor: '#f9f9f9' }}>Imaging Files</td>
                    <td style={{ padding: '6px' }}>{imagingFiles.length} file(s) uploaded</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '6px', fontWeight: '500', backgroundColor: '#f9f9f9' }}>Upload Date</td>
                    <td style={{ padding: '6px' }}>{new Date().toLocaleDateString('en-NZ')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
            <button
              onClick={() => navigate(`/patient-file/${result.patient?.id}`)}
              style={{ 
                padding: '10px 24px', backgroundColor: colors.dialogBg, color: 'white',
                border: 'none', cursor: 'pointer', fontSize: '12px'
              }}
            >
              View Patient File
            </button>
            <button
              onClick={() => {
                setResult(null);
                setFile(null);
                setImagingFiles([]);
                setEditedPatient(null);
              }}
              style={{ 
                padding: '10px 24px', backgroundColor: '#e0e0e0',
                border: '1px solid #999', cursor: 'pointer', fontSize: '12px'
              }}
            >
              Upload Another
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              style={{ 
                padding: '10px 24px', backgroundColor: '#e0e0e0',
                border: '1px solid #999', cursor: 'pointer', fontSize: '12px'
              }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Upload Form
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f0f0', fontFamily: 'Segoe UI, Tahoma, sans-serif' }}>
      {/* Header */}
      <div style={{ backgroundColor: colors.headerBg, borderBottom: '1px solid #ccc', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', backgroundColor: '#e0e0e0', border: '1px solid #999', cursor: 'pointer', fontSize: '12px' }}
        >
          <ArrowLeft style={{ width: '14px', height: '14px' }} />
          Back
        </button>
        <span style={{ fontWeight: '600' }}>Upload New Referral</span>
      </div>

      <div style={{ maxWidth: '600px', margin: '24px auto', padding: '0 16px' }}>
        {/* Upload Form Panel */}
        <div style={{ backgroundColor: 'white', border: '1px solid #ccc' }}>
          <div style={{ backgroundColor: colors.dialogBg, padding: '8px 16px', color: 'white', fontWeight: '600' }}>
            Referral Upload
          </div>
          
          <form onSubmit={handleUpload} style={{ padding: '16px' }}>
            {/* Injury Type Selection */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                Injury Type *
              </label>
              <select
                value={injuryType}
                onChange={(e) => setInjuryType(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', fontSize: '12px' }}
              >
                {injuryTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            {/* Referral Document Upload */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                Referral Document *
              </label>
              <div style={{ 
                border: '2px dashed #ccc', padding: '24px', textAlign: 'center',
                backgroundColor: file ? '#e8f5e9' : 'white', cursor: 'pointer'
              }}>
                <input
                  type="file"
                  accept=".pdf,.docx,.doc"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="file-upload"
                />
                <label htmlFor="file-upload" style={{ cursor: 'pointer' }}>
                  <FileText style={{ width: '36px', height: '36px', color: file ? colors.success : '#999', marginBottom: '8px' }} />
                  <div style={{ fontSize: '12px', color: file ? colors.success : '#666' }}>
                    {file ? `âœ“ ${file.name}` : 'Click to select PDF or Word document'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                    Accepts: .pdf, .docx, .doc
                  </div>
                </label>
              </div>
            </div>

            {/* Imaging Files Upload */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '6px' }}>
                Imaging Files (Optional)
              </label>
              <div style={{ 
                border: '2px dashed #ccc', padding: '16px', textAlign: 'center',
                backgroundColor: imagingFiles.length > 0 ? '#e3f2fd' : 'white', cursor: 'pointer'
              }}>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,.dcm"
                  onChange={handleImagingChange}
                  style={{ display: 'none' }}
                  id="imaging-upload"
                  multiple
                />
                <label htmlFor="imaging-upload" style={{ cursor: 'pointer' }}>
                  <Image style={{ width: '28px', height: '28px', color: imagingFiles.length > 0 ? colors.dialogBg : '#999', marginBottom: '4px' }} />
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {imagingFiles.length > 0 ? `${imagingFiles.length} file(s) - click to add more` : 'Click to add X-rays, MRI reports, etc.'}
                  </div>
                </label>
              </div>

              {/* Selected Imaging Files List */}
              {imagingFiles.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  {imagingFiles.map((imgFile, idx) => (
                    <div key={idx} style={{ 
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '6px 8px', backgroundColor: '#f5f5f5', marginBottom: '4px', fontSize: '11px'
                    }}>
                      <span>{imgFile.name}</span>
                      <button
                        type="button"
                        onClick={() => removeImagingFile(idx)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.danger, padding: '2px' }}
                      >
                        <X style={{ width: '14px', height: '14px' }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div style={{ 
                backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', padding: '8px 12px', 
                marginBottom: '16px', color: '#721c24', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px'
              }}>
                <AlertCircle style={{ width: '14px', height: '14px' }} />
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!file || uploading}
              style={{ 
                width: '100%', padding: '12px', 
                backgroundColor: (!file || uploading) ? '#ccc' : colors.success, 
                color: 'white', border: 'none', 
                cursor: (!file || uploading) ? 'not-allowed' : 'pointer',
                fontSize: '14px', fontWeight: '500',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
              }}
            >
              {uploading ? (
                <>
                  <div style={{ 
                    width: '16px', height: '16px', border: '2px solid white', borderTopColor: 'transparent',
                    borderRadius: '50%', animation: 'spin 1s linear infinite'
                  }} />
                  Processing...
                </>
              ) : (
                <>
                  <Upload style={{ width: '16px', height: '16px' }} />
                  Upload Referral
                </>
              )}
            </button>
          </form>
        </div>

        {/* Info Section */}
        <div style={{ backgroundColor: 'white', border: '1px solid #ccc', marginTop: '16px' }}>
          <div style={{ backgroundColor: colors.tableHeader, padding: '8px 16px', fontWeight: '600', fontSize: '12px' }}>
            What happens next
          </div>
          <div style={{ padding: '16px', fontSize: '12px', color: '#666' }}>
            <ol style={{ margin: 0, paddingLeft: '20px' }}>
              <li style={{ marginBottom: '6px' }}>Patient information is automatically extracted from the document</li>
              <li style={{ marginBottom: '6px' }}>You can edit patient details if extraction is incomplete</li>
              <li style={{ marginBottom: '6px' }}>A unique form link is generated for the patient</li>
              <li style={{ marginBottom: '6px' }}>Imaging files are stored in the patient's file</li>
              <li>Share the link with the patient to complete their medical history</li>
            </ol>
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default UploadReferralPage;
